import {useGeocoder, useGoogleMap} from '@ubilabs/google-maps-react-hooks';
import React, {useEffect, useRef, useState, VoidFunctionComponent} from 'react';
import ReactMarkdown from 'react-markdown';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';

import useDebounce from '../../../hooks/useDebounce';

import {WorkCategory} from '../../../../../shared/types/work';

import styles from './citizen-science-screen.module.css';
import {useLocalStorage} from '../../../hooks/useLocalStorage';
import Checkbox from '../checkbox/checkbox';
import Modal from '../flash-message/modal';

const CitizenScienceScreen: VoidFunctionComponent<Record<string, unknown>> =
  () => {
    const history = useHistory();
    const baseApiUrl = process.env.BASE_API_URL;
    const intl = useIntl();
    const {map} = useGoogleMap();
    const geocoder = useGeocoder();
    const [title, setTitle] = useLocalStorage<string>('title', '');
    const [artist, setArtist] = useLocalStorage<string>('artist', '');
    const [userName, setUserName] = useLocalStorage<string>('userName', '');
    const [userEmail, setUserEmail] = useLocalStorage<string>('userEmail', '');
    const [year, setYear] = useLocalStorage<string>('year', '');
    const [description, setDescription] = useLocalStorage<string>(
      'description',
      ''
    );
    const [category, setCategory] = useLocalStorage<string>('category', '');
    const [street, setStreet] = useLocalStorage<string>('street', '');
    const [city, setCity] = useLocalStorage<string>('city', '');
    const [postalCode, setPostalCode] = useLocalStorage<string>(
      'postalCode',
      ''
    );
    const [files, setFiles] = useState<FileList | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
    const [workPosition, setWorkPosition] =
      useLocalStorage<google.maps.LatLngLiteral>('workPosition', null);
    const [userConsent, setUserConsent] = useLocalStorage<boolean>(
      'userConsent',
      false
    );
    const sendDisabled = !userConsent;

    const debouncedSearchTerm = useDebounce(
      [city, street, postalCode].join(' ')
    );

    const marker = useRef<google.maps.Marker | null>(null);

    const save = async () => {
      const formData = new FormData();

      if (files) {
        Array.from(files).forEach(file => {
          formData.append('files', file);
        });
      }

      formData.append('title', title);
      formData.append('artist', artist);
      formData.append('year', year);
      formData.append('description.de', description);
      formData.append('category', category);
      formData.append('latitude', String(workPosition?.lat));
      formData.append('longitude', String(workPosition?.lng));
      formData.append('street', street);
      formData.append('city', city);
      formData.append('postalCode', postalCode);
      formData.append('userName', userName);
      formData.append('userEmail', userEmail);
      formData.append('userConsent', String(userConsent));

      const response = await fetch(`${baseApiUrl}/works/citizen-science`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        return;
      }

      window.localStorage.clear();

      setSubmitSuccess(true);
    };

    const removeMarker = () => {
      marker.current?.setMap(null);
      marker.current = null;
    };

    const onMessageClose = () => {
      setSubmitSuccess(false);
      history.push('/');
    };

    const createMarker = (position: google.maps.LatLngLiteral) => {
      if (map) {
        marker.current = new google.maps.Marker({
          map,
          position,
          draggable: true
        });

        marker.current.addListener('dragend', ({latLng}) => {
          setWorkPosition({
            lat: Number(latLng.lat().toFixed(7)),
            lng: Number(latLng.lng().toFixed(7))
          });
        });

        setWorkPosition(position);
      }
    };

    const updateMarkerPosition = (position: google.maps.LatLngLiteral) => {
      if (map) {
        if (!marker.current) {
          createMarker(position);
        } else {
          marker.current.setPosition(position);
        }

        map.setCenter(position);
        map.setZoom(15);
      }
    };

    const setMapCenter = () => {
      navigator.geolocation.getCurrentPosition(({coords}) => {
        if (coords.latitude && coords.longitude) {
          const latLng = {lat: coords.latitude, lng: coords.longitude};
          setWorkPosition(latLng);
          map?.setCenter(latLng);
          updateMarkerPosition(latLng);
        }
      });
    };

    /**
     * Create the map marker and set the map center to the marker position.
     */
    useEffect(() => {
      if (map) {
        createMarker(map.getCenter().toJSON());
      }

      return () => {
        removeMarker();
      };
    }, [map]);

    /**
     * Update the marker position when address changes the work position.
     * */
    useEffect(() => {
      if (workPosition) {
        updateMarkerPosition(workPosition);
      }
    }, [workPosition]);

    /**
     * Geocode the address and set work position.
     */
    useEffect(() => {
      geocoder?.geocode({address: debouncedSearchTerm}, (results, status) => {
        if (status === 'OK') {
          const {lat, lng} = results[0].geometry.location;

          setWorkPosition({lat: lat(), lng: lng()});
        }
      });
    }, [debouncedSearchTerm]);

    return (
      <>
        <div className={styles.container}>
          <h1>
            <FormattedMessage id="citizenScience.headline" />
          </h1>

          <article>
            <ReactMarkdown
              children={intl.formatMessage({id: 'citizenScience.infoText'})}
            />
          </article>

          <div className="form-field">
            <label htmlFor="title">
              <FormattedMessage id="citizenScience.title" />
            </label>
            <input
              id="title"
              name="title"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="artist">
              <FormattedMessage id="citizenScience.artist" />
            </label>
            <input
              id="artist"
              name="artist"
              value={artist}
              onChange={e => setArtist(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="category">
              <FormattedMessage id="citizenScience.category" />
            </label>
            <select
              id="category"
              name="category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              required>
              <option value="choose">
                {intl.formatMessage({
                  id: 'citizenScience.category.chooseCategory'
                })}
              </option>
              {Object.values(WorkCategory).map(workCategory => (
                <option value={workCategory} key={workCategory}>
                  {intl.formatMessage({
                    id: `work.category.${workCategory}`
                  })}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="year">
              <FormattedMessage id="citizenScience.date" />
            </label>
            <input
              id="year"
              name="year"
              pattern=".*\d{4}.*"
              value={year}
              onChange={e => setYear(e.target.value)}
              placeholder="YYYY-MM-DD"
            />
          </div>
          <div className="form-field">
            <label htmlFor="street">
              <FormattedMessage id="citizenScience.address" />
            </label>
            <input
              id="street"
              name="street"
              value={street}
              onChange={e => setStreet(e.target.value)}
              placeholder={intl.formatMessage({id: 'citizenScience.street'})}
            />
          </div>

          <div className="form-field">
            <input
              id="postalCode"
              name="postalCode"
              onChange={e => setPostalCode(e.target.value)}
              value={postalCode}
              placeholder={intl.formatMessage({
                id: 'citizenScience.postalCode'
              })}
            />
            <input
              id="city"
              name="city"
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder={intl.formatMessage({id: 'citizenScience.city'})}
            />
          </div>
          <button className="button-secondary" onClick={setMapCenter}>
            <FormattedMessage id="citizenScience.useCurrentPosition" />
          </button>
          <div className="form-field">
            <label htmlFor="description">
              <FormattedMessage id="citizenScience.description" />
            </label>
            <textarea
              id="description"
              name="description"
              value={description}
              onChange={e => setDescription(e.target.value)}></textarea>
          </div>
          <div className="form-field">
            <label htmlFor="files">
              <FormattedMessage id="citizenScience.pictures" />
            </label>
            <input
              className="files"
              type="file"
              id="files"
              name="files"
              accept="image/jpeg,image/jpg,image/png"
              multiple
              onChange={e => setFiles(e.target.files)}
            />

            {files && (
              <div className={styles.previewImages}>
                {Array.from(files).map(file => (
                  <img
                    className={styles.preview}
                    src={URL.createObjectURL(file)}
                    alt={`${file.name} preview`}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="form-field">
            <label htmlFor="userName">
              <FormattedMessage id="citizenScience.userName" />
            </label>
            <input
              id="userName"
              name="userName"
              value={userName}
              onChange={e => setUserName(e.target.value)}
              placeholder={intl.formatMessage({id: 'citizenScience.userName'})}
            />
          </div>

          <div className="form-field">
            <label htmlFor="userEmail">
              <FormattedMessage id="citizenScience.userEmail" />
            </label>
            <input
              id="userEmail"
              name="userEmail"
              value={userEmail}
              onChange={e => setUserEmail(e.target.value)}
              placeholder={intl.formatMessage({id: 'citizenScience.userEmail'})}
            />
          </div>

          <div className="form-field">
            <Checkbox
              checked={userConsent}
              label={intl.formatMessage({id: 'citizenScience.userConsent'})}
              name="userConsent"
              value="userConsent"
              onChange={() => setUserConsent(!userConsent)}
            />
          </div>

          <button
            disabled={sendDisabled}
            className="button-primary"
            onClick={save}>
            <FormattedMessage id="citizenScience.save" />
          </button>
        </div>
        {submitSuccess && (
          <Modal
            message={intl.formatMessage({id: 'citizenScience.successMessage'})}
            onClose={onMessageClose}
          />
        )}
      </>
    );
  };

export default CitizenScienceScreen;
